/**
 * API client.
 *
 * Everything is same-origin: the panels are served from the venue's own
 * subdomain, so the session cookie travels automatically and there is no CORS or
 * token plumbing to get wrong.
 */

/** The error envelope the backend returns for every failure. */
export interface ApiErrorBody {
  code: string;
  message: string;
  request_id?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.requestId = body.request_id;
  }

  /** True when the session is missing or expired, which means "show the login". */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True when the API itself could not be reached or answered: nothing the user did. */
  get isUnreachable(): boolean {
    return this.status === 0 || this.status >= 502;
  }
}

const UNREACHABLE = "Brak połączenia z serwerem. Spróbuj ponownie za chwilę.";

/**
 * Turns a failed response into an ApiError with a message a person can read.
 * The backend always answers JSON, so a non-JSON or empty body means the
 * request never reached it (proxy, outage); that must not surface as an empty
 * string or a parser's complaint.
 */
async function failure(response: Response): Promise<ApiError> {
  const text = await response.text();
  let body: Partial<ApiErrorBody> = {};
  try {
    body = text ? (JSON.parse(text) as Partial<ApiErrorBody>) : {};
  } catch {
    body = {};
  }
  if (body.message) return new ApiError(response.status, body as ApiErrorBody);
  const message = response.status >= 500 ? UNREACHABLE : `Nieoczekiwana odpowiedź serwera (${response.status}).`;
  return new ApiError(response.status, { code: body.code ?? "unexpected_response", message });
}

/** A fetch that never resolved (offline, DNS, refused) becomes a status-0 ApiError. */
function unreachable(error: unknown): ApiError {
  if (error instanceof DOMException && error.name === "AbortError") throw error;
  return new ApiError(0, { code: "network", message: UNREACHABLE });
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Bearer token for the scanner surfaces, which do not use cookies. */
  token?: string;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, token, signal } = options;

  const headers: Record<string, string> = {};
  // Only send a content type when there is a body: the API rejects an empty
  // JSON body, so a bare POST must not claim to carry one.
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers["authorization"] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // Same-origin cookies; the API sets them HttpOnly.
      credentials: "same-origin",
      signal,
    });
  } catch (error) {
    throw unreachable(error);
  }

  if (!response.ok) throw await failure(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export const get = <T,>(path: string, signal?: AbortSignal, token?: string) => api<T>(path, { signal, token });
export const post = <T,>(path: string, body?: unknown, token?: string) =>
  api<T>(path, { method: "POST", body, token });
export const patch = <T,>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T,>(path: string) => api<T>(path, { method: "DELETE" });

/** Multipart upload of one file under the field name the API expects. */
export async function upload<T>(path: string, field: string, file: File, method: "PUT" | "POST" = "PUT"): Promise<T> {
  const body = new FormData();
  body.append(field, file);
  let response: Response;
  try {
    // No content-type header: the browser sets the multipart boundary itself.
    response = await fetch(path, { method, body, credentials: "same-origin" });
  } catch (error) {
    throw unreachable(error);
  }
  if (!response.ok) throw await failure(response);
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}
